import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { PieChart, Pie, Cell, ResponsiveContainer, Tooltip } from 'recharts';
import axiosInstance from '../../../../../shared/services/api/axiosInstance';
import { formatNumber } from '../../../../../shared/utils/numberFormatter';

const WalletAgeDistributionChart = ({ teamId, appliedFilters, isLoading: parentLoading }) => {
  const [distributionData, setDistributionData] = useState([]);
  const [walletStats, setWalletStats] = useState({ averageAge: '', medianAge: '' });
  const [isLoading, setIsLoading] = useState(false);

  const styles = {
    primaryBg: "#ffffff",
    secondaryBg: "#DDE2EE",
    textPrimary: "#0f172a",
    textSecondary: "#666666",
    accentColor: "#1d0c46",
    borderColor: "#DDE2EE"
  };

  // Color palette for pie chart segments
  const COLORS = [
    '#1d0c46', // Dark purple
    '#4a2c7a', // Medium purple
    '#7a4fa8', // Light purple
    '#a67dd6'  // Very light purple
  ];

  // Filter out empty buckets (only show buckets with walletCount > 0)
  const filteredData = useMemo(() => {
    if (!distributionData || distributionData.length === 0) return [];
    return distributionData.filter(item => item.walletCount > 0);
  }, [distributionData]);

  // Fetch wallet age distribution data
  const fetchWalletAgeDistribution = useCallback(async () => {
    if (!teamId || parentLoading) {
      return;
    }

    setIsLoading(true);
    try {
      const params = { 
        team_id: teamId
      };

      // Add contract filter if selected
      if (appliedFilters.contracts && appliedFilters.contracts.length > 0) {
        params.contracts = appliedFilters.contracts.map(c => c.id || c).join(',');
      }

      const response = await axiosInstance.get('/overview/wallet-age-distribution', { params });

      if (response.data?.success) {
        setDistributionData(response.data.data || []);
        setWalletStats(response.data.stats || { averageAge: '', medianAge: '' });
      } else {
        console.warn('Wallet age distribution response not successful:', response.data);
        setDistributionData([]);
        setWalletStats({ averageAge: '', medianAge: '' });
      }
    } catch (error) {
      console.error('Error fetching wallet age distribution:', error);
      setDistributionData([]);
    } finally {
      setIsLoading(false);
    }
  }, [teamId, appliedFilters.contracts, parentLoading]);

  useEffect(() => {
    fetchWalletAgeDistribution();
  }, [fetchWalletAgeDistribution]);

  // Custom Tooltip Component
  const CustomTooltip = ({ active, payload }) => {
    if (active && payload && payload.length) {
      const data = payload[0];
      const total = filteredData.reduce((sum, item) => sum + item.walletCount, 0);
      const percentage = total > 0 ? ((data.payload.walletCount / total) * 100).toFixed(1) : 0;

      return (
        <div 
          className="bg-white p-3 border shadow-lg rounded-lg"
          style={{ 
            borderColor: styles.borderColor,
            fontFamily: 'Raleway, sans-serif'
          }}
        >
          <p className="text-sm font-medium mb-1" style={{ color: styles.textPrimary }}>
            {data.payload.ageBucket}
          </p>
          <p className="text-xs" style={{ color: styles.textSecondary }}>
            Wallets: <span className="font-medium">{formatNumber(data.payload.walletCount)}</span>
          </p>
          <p className="text-xs" style={{ color: styles.textSecondary }}>
            Percentage: <span className="font-medium">{percentage}%</span>
          </p>
        </div>
      );
    }
    return null;
  };

  if (isLoading || parentLoading) {
    return (
      <div 
        className="p-4 md:p-6"
        style={{ backgroundColor: styles.primaryBg, border: `1px solid ${styles.borderColor}` }}
      >
        <div className="flex items-center justify-center h-64">
          <div className="text-sm" style={{ color: styles.textSecondary, fontFamily: 'Raleway, sans-serif' }}>
            Loading wallet age distribution...
          </div>
        </div>
      </div>
    );
  }

  if (!filteredData || filteredData.length === 0) {
    return (
      <div 
        className="p-4 md:p-6"
        style={{ backgroundColor: styles.primaryBg, border: `1px solid ${styles.borderColor}` }}
      >
        <div className="mb-4">
          <h3 
            className="text-sm font-medium"
            style={{ color: styles.accentColor, fontFamily: 'Raleway, sans-serif' }}
          >
            Wallet Age Distribution
          </h3>
        </div>
        <div className="flex items-center justify-center h-64">
          <div className="text-sm" style={{ color: styles.textSecondary, fontFamily: 'Raleway, sans-serif' }}>
            No wallet data available
          </div>
        </div>
      </div>
    );
  }

  return (
    <div 
      className="p-2 md:p-3"
      style={{ backgroundColor: styles.primaryBg, border: `1px solid ${styles.borderColor}` }}
    >
      <div className="mb-2">
        <h3 
          className="text-sm font-medium"
          style={{ color: styles.accentColor, fontFamily: 'Raleway, sans-serif' }}
        >
          Wallet Age Distribution
        </h3>
      </div>

      <div style={{ height: '250px', width: '100%' }}>
        <ResponsiveContainer width="100%" height="100%">
          <PieChart margin={{ top: 0, right: 0, bottom: 0, left: 0 }}>
            <Pie
              data={filteredData}
              cx="50%"
              cy="50%"
              labelLine={false}
              label={({ ageBucket, percent }) => 
                `${ageBucket}: ${(percent * 100).toFixed(0)}%`
              }
              outerRadius={60}
              innerRadius={30}
              fill={styles.accentColor}
              dataKey="walletCount"
              nameKey="ageBucket"
            >
              {filteredData.map((entry, index) => (
                <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
              ))}
            </Pie>
            <Tooltip content={<CustomTooltip />} />
          </PieChart>
        </ResponsiveContainer>
      </div>

      {/* Average and Median Wallet Age */}
      {walletStats.averageAge && walletStats.medianAge && (
        <div className="flex justify-around items-center mt-1 pt-1" style={{ borderTop: `1px solid ${styles.borderColor}` }}>
          <div className="text-center">
            <div className="text-xs mb-1" style={{ color: styles.textSecondary, fontFamily: 'Raleway, sans-serif' }}>
              Average Age
            </div>
            <div className="text-sm font-medium" style={{ color: styles.accentColor, fontFamily: 'Raleway, sans-serif' }}>
              {walletStats.averageAge}
            </div>
          </div>
          <div className="text-center">
            <div className="text-xs mb-1" style={{ color: styles.textSecondary, fontFamily: 'Raleway, sans-serif' }}>
              Median Age
            </div>
            <div className="text-sm font-medium" style={{ color: styles.accentColor, fontFamily: 'Raleway, sans-serif' }}>
              {walletStats.medianAge}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default WalletAgeDistributionChart;

